import React, { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Plus, Trash, CalendarBlank, Table, Warning, Keyboard } from "@phosphor-icons/react";

export const toIsoLocal = (d) => (d ? format(d, "yyyy-MM-dd") : "");
const isoToDate = (s) => (s ? new Date(s + "T00:00:00") : undefined);

const COLS = [
  { key: "payment_type", label: "Payment Type", w: "w-36", list: "pg-types" },
  { key: "mode_of_payment", label: "Mode", w: "w-28", list: "pg-modes" },
  { key: "vendor", label: "Vendor / Payee *", w: "w-52", list: "pg-vendors" },
  { key: "cost_center", label: "Cost Centre", w: "w-32", list: "pg-cc" },
  { key: "po", label: "PO # / Ref *", w: "w-40", list: "pg-pos" },
  { key: "item", label: "Item / Purpose", w: "w-56", list: "pg-items" },
  { key: "amount", label: "Amount *", w: "w-28", num: true },
  { key: "last_amount", label: "Last Paid Amt", w: "w-32", num: true },
  { key: "last_date", label: "Last Paid Date", w: "w-36", date: true },
  { key: "cheque_no", label: "Cheque No", w: "w-28" },
  { key: "narration", label: "Narration", w: "w-60", list: "pg-narrations" },
];
const FIELD_KEYS = COLS.map((c) => c.key);
const AUTO_KEYS = ["last_amount", "last_date"];

let _rid = 0;
const blankRow = () => ({ _id: ++_rid, ...Object.fromEntries(FIELD_KEYS.map((k) => [k, ""])), error: null });
const isBlank = (r) => FIELD_KEYS.filter((k) => !AUTO_KEYS.includes(k)).every((k) => !String(r[k] || "").trim());
const START_ROWS = 5;

const fromRecord = (rec) => ({
  ...blankRow(),
  id: rec.id, status: rec.chain_status, date: (rec.date || "").slice(0, 10), _poId: rec.po_id || null, _lpTouched: true,
  payment_type: rec.payment_type || "", mode_of_payment: rec.mode_of_payment || "",
  vendor: rec.vendor || "", cost_center: rec.cost_center || "",
  po: rec.po_number || rec.manual_po_ref || "", item: rec.item || "",
  amount: rec.amount != null ? String(rec.amount) : "",
  last_amount: rec.last_amount_paid != null ? String(rec.last_amount_paid) : "",
  last_date: (rec.last_payment_date || "").slice(0, 10),
  cheque_no: rec.cheque_no || "", narration: rec.narration || "",
});

function rowProblem(r) {
  if (!r.vendor.trim()) return "Vendor required";
  if (!(Number(r.amount) > 0)) return "Amount required";
  if (!r.po.trim()) return "PO # or reference required";
  return null;
}

export function PaymentEntryGrid({ open, onClose, onSaved, editRows }) {
  const [master, setMaster] = useState(null);
  const [date, setDate] = useState(toIsoLocal(new Date()));
  const [dateOpen, setDateOpen] = useState(false);
  const [rows, setRows] = useState(() => Array.from({ length: START_ROWS }, blankRow));
  const lastCache = useRef({});
  const [busy, setBusy] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    api.get("/payment-requests/master-data").then((r) => setMaster(r.data))
      .catch(() => toast.error("Could not load dropdown lists"));
  }, [open]);

  const isEdit = !!editRows?.length;
  useEffect(() => {
    if (!open) return;
    if (editRows?.length) {
      setRows([...editRows.map(fromRecord), blankRow()]);
      setDate((editRows[0].date || "").slice(0, 10) || toIsoLocal(new Date()));
    } else {
      setRows(Array.from({ length: START_ROWS }, blankRow));
      setDate(toIsoLocal(new Date()));
    }
  }, [open, editRows]);

  const poByNumber = useMemo(() => {
    const m = {};
    (master?.open_pos || []).forEach((p) => { if (p.po_number) m[p.po_number.toLowerCase()] = p; });
    return m;
  }, [master]);

  const vendorSet = useMemo(() => new Set((master?.vendors || []).map((v) => v.toLowerCase())), [master]);
  const ccSet = useMemo(() => new Set((master?.cost_centers || []).map((v) => v.toLowerCase())), [master]);

  const patchRow = (rid, fn) => setRows((rs) => rs.map((r) => (r._id === rid ? fn(r) : r)));

  const fetchLast = async (rid, vendor, outlet) => {
    const v = (vendor || "").trim();
    if (!v) return;
    const key = `${v.toLowerCase()}|${(outlet || "").trim().toLowerCase()}`;
    if (!lastCache.current[key]) {
      lastCache.current[key] = api.get("/payment-requests/last-payment", { params: { vendor: v, outlet: (outlet || "").trim() || undefined } })
        .then((r) => r.data).catch(() => null);
    }
    const d = await lastCache.current[key];
    if (!d) return;
    patchRow(rid, (r) => {
      if (r.vendor.trim().toLowerCase() !== v.toLowerCase()) return r;
      const n = { ...r, _lp: d };
      if (!r._lpTouched) {
        n.last_amount = d.last_amount != null ? String(d.last_amount) : "";
        n.last_date = d.last_date ? String(d.last_date).slice(0, 10) : "";
      }
      if (!r.payment_type && d.last_payment_type) n.payment_type = d.last_payment_type;
      if (!r.mode_of_payment && d.last_mode_of_payment) n.mode_of_payment = d.last_mode_of_payment;
      return n;
    });
  };

  const onPoCommit = async (rid, value) => {
    const po = poByNumber[value.trim().toLowerCase()];
    const row = rows.find((r) => r._id === rid);
    if (!po || !row || row._poId === po.id) return;
    const vendor = row.vendor || po.vendor || "";
    const outlet = row.cost_center || po.outlet || "";
    patchRow(rid, (r) => ({
      ...r, _poId: po.id, vendor, cost_center: outlet,
      amount: r.amount || (po.amount != null ? String(po.amount) : ""),
    }));
    fetchLast(rid, vendor, outlet);
    try {
      const res = await api.get(`/payment-requests/po/${po.id}/line-items`);
      const items = (res.data?.items || []).map((li) => li.label).join(", ");
      if (items) patchRow(rid, (r) => (r.item ? r : { ...r, item: items }));
    } catch { /* line items are optional */ }
  };

  const setCell = (rid, key, val) => {
    const row = rows.find((r) => r._id === rid);
    patchRow(rid, (r) => ({ ...r, [key]: val, error: null, ...(AUTO_KEYS.includes(key) ? { _lpTouched: true } : {}) }));
    if (!row) return;
    const low = val.trim().toLowerCase();
    if (key === "po" && poByNumber[low]) onPoCommit(rid, val);
    if (key === "vendor" && vendorSet.has(low)) fetchLast(rid, val, row.cost_center);
    if (key === "cost_center" && ccSet.has(low) && row.vendor.trim()) fetchLast(rid, row.vendor, val);
  };

  const onCellBlur = (r, key, val) => {
    if (key === "po") onPoCommit(r._id, val);
    if (key === "vendor") fetchLast(r._id, val, r.cost_center);
    if (key === "cost_center" && r.vendor.trim()) fetchLast(r._id, r.vendor, val);
  };

  const focusCell = (ri, ci) => {
    const el = gridRef.current?.querySelector(`[data-cell="${ri}-${ci}"]`);
    if (el) { el.focus(); el.select?.(); }
  };

  const onKeyDown = (e, ri, ci) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submit(false); return; }
    const move = { Enter: [1, 0], ArrowDown: [1, 0], ArrowUp: [-1, 0] }[e.key];
    if (!move || (e.key !== "Enter" && e.altKey)) return;
    e.preventDefault();
    const nr = ri + move[0];
    if (nr >= rows.length) { setRows((rs) => [...rs, blankRow()]); setTimeout(() => focusCell(nr, ci), 0); return; }
    if (nr >= 0) focusCell(nr, ci);
  };

  const onPaste = (e, ri, ci) => {
    const text = e.clipboardData.getData("text");
    if (!text.includes("\t") && !text.includes("\n")) return;
    e.preventDefault();
    const lines = text.replace(/\r/g, "").split("\n").filter((l, i, a) => l || i < a.length - 1);
    setRows((rs) => {
      const next = [...rs];
      lines.forEach((line, li) => {
        const idx = ri + li;
        if (!next[idx]) next[idx] = blankRow();
        const r = { ...next[idx], error: null };
        line.split("\t").forEach((val, vi) => {
          const col = COLS[ci + vi];
          if (!col) return;
          r[col.key] = col.num ? val.replace(/[₹,\s]/g, "") : val.trim();
          if (AUTO_KEYS.includes(col.key)) r._lpTouched = true;
        });
        next[idx] = r;
      });
      return next;
    });
    toast.success(`Pasted ${lines.length} row(s)`);
  };

  const filled = rows.filter((r) => !isBlank(r));
  const total = filled.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const submit = async (asDraft) => {
    if (!date) { toast.error("Pick the payment date"); return; }
    if (filled.length === 0) { toast.error("Enter at least one row"); return; }
    const problems = filled.map((r) => [r._id, rowProblem(r)]).filter(([, p]) => p);
    if (problems.length) {
      const pm = Object.fromEntries(problems);
      setRows((rs) => rs.map((r) => (pm[r._id] ? { ...r, error: pm[r._id] } : r)));
      toast.error(`${problems.length} row(s) need fixing`);
      return;
    }
    setBusy(true);
    const knownModes = new Set((master?.payment_modes || []).map((m) => m.toLowerCase()));
    const newModes = [...new Set(filled.map((r) => r.mode_of_payment.trim()).filter((m) => m && !knownModes.has(m.toLowerCase())))];
    await Promise.all(newModes.map((name) => api.post("/payment-requests/payment-modes", { name }).catch(() => null)));

    const failed = {};
    let ok = 0;
    for (const r of filled) {
      const po = poByNumber[r.po.trim().toLowerCase()];
      const payload = {
          payment_type: r.payment_type.trim(), mode_of_payment: r.mode_of_payment.trim(),
          vendor: r.vendor.trim(), cost_center: r.cost_center.trim(), item: r.item.trim(),
          po_id: po?.id || null, po_number: po ? po.po_number : "",
          no_po_flag: !po, manual_po_ref: po ? "" : r.po.trim(),
          date: r.date || date, amount: Number(r.amount),
          cheque_no: r.cheque_no.trim(), narration: r.narration.trim(),
          last_amount_paid: r.last_amount !== "" ? Number(r.last_amount) : null,
          last_payment_date: r.last_date || null,
      };
      try {
        if (r.id) {
          await api.put(`/payment-requests/${r.id}`, payload);
          if (!asDraft && r.status === "draft") await api.post(`/payment-requests/${r.id}/submit`);
        } else {
          await api.post("/payment-requests", { ...payload, draft: asDraft });
        }
        ok += 1;
      } catch (e) {
        failed[r._id] = e?.response?.data?.detail || "Submit failed";
      }
    }
    setBusy(false);
    const remaining = rows.filter((r) => failed[r._id]).map((r) => ({ ...r, error: failed[r._id] }));
    if (ok) onSaved?.();
    if (!remaining.length) {
      toast.success(asDraft ? `${ok} row(s) saved as draft` : `${ok} payment request(s) submitted for approval`);
      onClose();
      return;
    }
    setRows([...remaining, blankRow()]);
    toast.error(`${ok} submitted · ${remaining.length} failed. Fix the rows shown and resubmit`, { duration: 9000 });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-[96vw] w-[96vw] bg-slate-950 border-slate-800 text-slate-100 p-5"
        data-testid="pmt-grid-dialog">
        <DialogHeader>
          <DialogTitle className="text-cyan-100 flex items-center gap-2">
            <Table size={16} weight="duotone" className="text-cyan-300" />
            {isEdit ? "Edit Payment Requests" : "New Payment Approval Requests"}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-[11px]">
            Enter one payment per row, like a spreadsheet. Every row goes through the L1 → L2 → L3 approval chain.
          </DialogDescription>
        </DialogHeader>

        <GridToolbar date={date}
          mixed={new Set(rows.filter((r) => r.date).map((r) => r.date)).size > 1}
          setDate={(d) => { setDate(d); setRows((rs) => rs.map((r) => (r.date ? { ...r, date: d } : r))); }} dateOpen={dateOpen} setDateOpen={setDateOpen}
          count={filled.length} total={total} />

        <Datalists master={master} />

        <div ref={gridRef} className="border border-slate-700 overflow-auto max-h-[58vh] bg-slate-950"
          data-testid="pmt-grid">
          <table className="border-collapse text-[11.5px] min-w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-900 text-[9.5px] uppercase tracking-wider font-bold text-cyan-200">
                <th className="w-9 border border-slate-700 px-1 py-1.5 text-slate-500">#</th>
                {COLS.map((c) => (
                  <th key={c.key} className={`${c.w} border border-slate-700 px-2 py-1.5 text-left whitespace-nowrap`}>{c.label}</th>
                ))}
                <th className="w-8 border border-slate-700" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <GridRow key={r._id} r={r} ri={ri} poByNumber={poByNumber}
                  setCell={setCell} onKeyDown={onKeyDown} onPaste={onPaste} onCellBlur={onCellBlur}
                  onRemove={() => setRows((rs) => (rs.length > 1 ? rs.filter((x) => x._id !== r._id) : [blankRow()]))} />
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between gap-3 pt-1 flex-wrap">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" onClick={() => setRows((rs) => [...rs, ...Array.from({ length: 5 }, blankRow)])}
              className="h-8 border-slate-700 text-slate-200 bg-slate-900 hover:bg-slate-800 text-[11px]"
              data-testid="pmt-grid-add-rows">
              <Plus size={12} weight="bold" className="mr-1" />Add 5 rows
            </Button>
            <span className="text-[10px] text-slate-500 flex items-center gap-1.5">
              <Keyboard size={12} /> Tab = next cell · Enter / ↓ = next row · Paste straight from Excel · Ctrl+Enter = submit
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={busy}
              className="h-9 border-slate-700 text-slate-300 bg-slate-900 hover:bg-slate-800"
              data-testid="pmt-grid-cancel">Cancel</Button>
            <Button variant="outline" onClick={() => submit(true)} disabled={busy || filled.length === 0}
              className="h-9 border-amber-700 text-amber-200 bg-amber-950/40 hover:bg-amber-900/50 font-semibold"
              data-testid="pmt-grid-save-draft">
              {busy ? "Saving…" : "Save as Draft"}
            </Button>
            <Button onClick={() => submit(false)} disabled={busy || filled.length === 0}
              className="h-9 bg-cyan-600 hover:bg-cyan-500 text-white font-semibold"
              data-testid="pmt-grid-submit">
              {busy ? "Submitting…" : `Submit ${filled.length || ""} for Approval`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GridToolbar({ date, mixed, setDate, dateOpen, setDateOpen, count, total }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">Payment Date * (applies to all rows)</div>
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-9 min-w-48 bg-slate-900 border-cyan-800 text-slate-100 justify-start font-mono text-[12px]"
              data-testid="pmt-grid-date-trigger">
              <CalendarBlank size={13} weight="bold" className="mr-2 text-cyan-300" />
              {mixed ? "Mixed dates (kept per row)" : date ? format(isoToDate(date), "dd MMM yyyy") : "Pick a date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 bg-slate-950 border-slate-700 text-slate-100 w-auto" align="start">
            <Calendar mode="single" selected={isoToDate(date)} initialFocus
              onSelect={(d) => { if (d) { setDate(toIsoLocal(d)); setDateOpen(false); } }} />
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex items-center gap-4 font-mono text-[11px]">
        <span className="text-slate-400">Rows: <b className="text-slate-100" data-testid="pmt-grid-row-count">{count}</b></span>
        <span className="text-slate-400">Total: <b className="text-cyan-200" data-testid="pmt-grid-total">{fmtINR(total)}</b></span>
      </div>
    </div>
  );
}

function Datalists({ master }) {
  const lists = {
    "pg-types": (master?.payment_types || []).map((v) => [v]),
    "pg-modes": (master?.payment_modes || []).map((v) => [v]),
    "pg-vendors": (master?.vendors || []).map((v) => [v]),
    "pg-cc": (master?.cost_centers || []).map((v) => [v]),
    "pg-items": (master?.upcoming_items || []).map((v) => [v]),
    "pg-narrations": (master?.narrations || []).map((v) => [v]),
    "pg-pos": (master?.open_pos || []).filter((p) => p.po_number).map((p) => [
      p.po_number, `${p.vendor} · ${p.outlet} · ${fmtINR(p.amount)}${p.is_approved ? "" : " · PO pending"}`,
    ]),
  };
  return Object.entries(lists).map(([id, opts]) => (
    <datalist id={id} key={id}>
      {opts.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </datalist>
  ));
}

function GridRow({ r, ri, poByNumber, setCell, onKeyDown, onPaste, onCellBlur, onRemove }) {
  const po = r.po.trim() ? poByNumber[r.po.trim().toLowerCase()] : null;
  return (
    <>
      <tr className={`group ${r.error ? "bg-rose-950/30" : "hover:bg-slate-900/50"}`} data-testid={`pmt-grid-row-${ri}`}>
        <td className="border border-slate-800 text-center text-[10px] text-slate-500 font-mono bg-slate-900/60">{ri + 1}</td>
        {COLS.map((c, ci) => (
          <td key={c.key} className="border border-slate-800 p-0 relative">
            <input
              data-cell={`${ri}-${ci}`}
              data-testid={`pmt-grid-cell-${ri}-${c.key}`}
              list={c.list}
              type={c.num ? "number" : c.date ? "date" : "text"}
              step={c.num ? "0.01" : undefined}
              value={r[c.key]}
              onChange={(e) => setCell(r._id, c.key, e.target.value)}
              onKeyDown={(e) => onKeyDown(e, ri, ci)}
              onPaste={(e) => onPaste(e, ri, ci)}
              onBlur={(e) => onCellBlur(r, c.key, e.target.value)}
              className={`w-full h-8 px-2 bg-transparent text-slate-100 outline-none [color-scheme:dark] ${c.key === "last_amount" && r._lp?.source ? "pr-12" : ""} focus:bg-cyan-950/40 focus:ring-2 focus:ring-inset focus:ring-cyan-500 placeholder:text-slate-700 ${c.num ? "text-right font-mono tabular-nums" : ""}`}
            />
            {c.key === "po" && r.po.trim() && (
              <span className={`absolute right-1 top-1/2 -translate-y-1/2 px-1 rounded-sm text-[8px] font-bold uppercase pointer-events-none ${po ? "bg-emerald-950 text-emerald-300" : "bg-amber-950 text-amber-300"}`}
                data-testid={`pmt-grid-po-badge-${ri}`}>
                {po ? (po.is_approved ? "PO" : "PO pend") : "No PO"}
              </span>
            )}
            {c.key === "last_amount" && r._lp?.source && !r._lpTouched && (
              <span title={`From ${r._lp.source}${r._lp.where ? ` · ${r._lp.where}` : ""}${r._lp.ref ? ` · ${r._lp.ref}` : ""}${r._lp.outlet_matched ? "" : " · other outlet (no match for this outlet)"}`}
                className={`absolute right-1 top-1/2 -translate-y-1/2 px-1 rounded-sm text-[8px] font-bold uppercase ${r._lp.outlet_matched ? "bg-emerald-950 text-emerald-300" : "bg-amber-950 text-amber-300"}`}
                data-testid={`pmt-grid-last-src-${ri}`}>
                {r._lp.source}
              </span>
            )}
          </td>
        ))}
        <td className="border border-slate-800 text-center">
          <button type="button" tabIndex={-1} onClick={onRemove}
            className="text-slate-600 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`pmt-grid-remove-${ri}`} title="Remove row">
            <Trash size={12} weight="bold" />
          </button>
        </td>
      </tr>
      {r.error && (
        <tr data-testid={`pmt-grid-row-error-${ri}`}>
          <td />
          <td colSpan={COLS.length + 1} className="px-2 py-1 text-[10.5px] text-rose-300 border-x border-b border-slate-800 bg-rose-950/30">
            <Warning size={11} weight="fill" className="inline mr-1" />{r.error}
          </td>
        </tr>
      )}
    </>
  );
}

